import { FormField, LightMode, Variables } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import { endOfDay, isAfter } from "date-fns";
import { useRef } from "react";
import ReactDatePicker from "react-datepicker";

import { DatePickerInput } from "./DatePicker";
import { DatePickerStyleContainer } from "./InlineDatePicker";

const TIME_FORMAT = "HH:mm";

interface ITImePickerProps {
  onChange: (date: Date) => void;
  value: Date | undefined;
  id: string;
  label: string;
  timeIntervals?: number;
  disabled?: boolean;
  minTime?: Date;
  isValid?: boolean;
  required?: boolean;
}

export const TimePicker = ({
  onChange,
  value,
  id,
  label,
  timeIntervals,
  disabled,
  minTime,
  isValid,
  required,
}: ITImePickerProps) => {
  const { dateFnsLocale } = useTranslation();
  const ref = useRef<ReactDatePicker>(null);

  const actualMinTime = minTime
    ? isAfter(minTime, new Date())
      ? minTime
      : undefined
    : undefined;

  return (
    <DatePickerStyleContainer
      border={`${Variables.thicknessThin} solid ${LightMode.colourNeutral3}`}
      className="saleslead__element--focus-visible"
    >
      <FormField
        shouldValidate={isValid === false}
        valid={isValid}
        validation={{
          msg: t("prompt.invalid_time"),
          id: "error",
          type: "error",
        }}
      >
        <ReactDatePicker
          id={`time-${id}`}
          readOnly={false}
          ref={ref}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              ref?.current?.setOpen(false);
            }
          }}
          customInput={
            <DatePickerInput
              label={label}
              id={`time-${id}-input`}
              required={required}
            />
          }
          disabled={disabled}
          strictParsing
          onChange={(d: Date | null) => {
            if (d instanceof Date && !Number.isNaN(d.getTime())) {
              const newDate = new Date(value ?? new Date());
              newDate.setHours(d.getHours());
              newDate.setMinutes(d.getMinutes());
              newDate.setSeconds(0);
              newDate.setMilliseconds(0);
              onChange(newDate);
            }
          }}
          locale={dateFnsLocale}
          minTime={actualMinTime}
          maxTime={actualMinTime ? endOfDay(actualMinTime) : undefined}
          timeFormat={TIME_FORMAT}
          dateFormat={TIME_FORMAT}
          selected={value}
          timeCaption={t("label.time")}
          timeIntervals={timeIntervals}
          showTimeSelect
          showTimeSelectOnly
        />
      </FormField>
    </DatePickerStyleContainer>
  );
};
